import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { API_DOCS } from './ListiniForniPage.api';

// ── Interfacce ────────────────────────────────────────────────────────────────

interface ImportListino {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: 'IN_CORSO' | 'COMPLETATO' | 'COMPLETATO_CON_ERRORI' | 'ERRORE';
  importatoAt: string;
  totaleRighe: number;
  righeCreate: number;
  righeAggiornate: number;
  righeErrore: number;
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null };
  utente: { id: number; nome: string; email: string };
}

interface Fornitore {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
}

const LIMIT = 20;

// ── Helper badge ──────────────────────────────────────────────────────────────

const badgeStato = (stato: string) => {
  const map: Record<string, string> = {
    COMPLETATO:             'bop-badge-success',
    COMPLETATO_CON_ERRORI:  'bop-badge-warning',
    ERRORE:                 'bop-badge-danger',
    IN_CORSO:               'bop-badge-neutral',
  };
  const labels: Record<string, string> = {
    COMPLETATO:            'Completato',
    COMPLETATO_CON_ERRORI: 'Con errori',
    ERRORE:                'Errore',
    IN_CORSO:              'In corso',
  };
  return (
    <span className={`bop-badge ${map[stato] || 'bop-badge-neutral'}`}>
      {labels[stato] || stato}
    </span>
  );
};

// ── Componente principale ─────────────────────────────────────────────────────

export default function ListiniForniPage() {
  const { societaId: filterSocietaId, pronta } = useFiltroSocieta();

  // ── Stato ───────────────────────────────────────────────────────────────────
  const [allData,     setAllData]     = useState<ImportListino[]>([]);
  const [totalAll,    setTotalAll]    = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [fornitori,   setFornitori]   = useState<Fornitore[]>([]);
  const [fornitoreId, setFornitoreId] = useState('');
  const [importId,    setImportId]    = useState('');
  const [page,        setPage]        = useState(1);

  // ── Carica fornitori ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!pronta) return;
    // @api GET /api/nominativi?ruolo=FORNITORE&limit=200&societaId=N -> {"data":"Nominativo[]","total":0}
    apiFetch<{ data: Fornitore[] }>(
      `/api/nominativi?ruolo=FORNITORE&limit=200${filterSocietaId ? `&societaId=${filterSocietaId}` : ''}`
    ).then(r => setFornitori(r.data ?? [])).catch(console.error);
  }, [pronta, filterSocietaId]);

  // ── Carica elenco listini (limit=200, client-side filtering) ────────────
  const caricaDati = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '200' });
      if (filterSocietaId) params.set('societaId', String(filterSocietaId));
      if (fornitoreId)     params.set('fornitoreId', fornitoreId);
      // @api GET /api/anagrafiche/listini?societaId=N&fornitoreId=N&page=1&limit=20 -> {"success":true,"data":"ImportListino[]","total":"number","page":"number","limit":"number"}
      const res = await apiFetch<{ success: boolean; data: ImportListino[]; total: number }>(
        `/api/anagrafiche/listini?${params}`
      );
      setAllData(res.data);
      setTotalAll(res.total);
      setPage(1);
    } catch (err) {
      console.error('[ListiniForniPage] caricaDati', err);
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId, fornitoreId]);

  useEffect(() => {
    caricaDati();
  }, [pronta, filterSocietaId, fornitoreId]); // eslint-disable-line

  // Reset importazione when fornitore changes
  useEffect(() => {
    setImportId('');
  }, [fornitoreId]);

  // ── Filtraggio client-side per importazione selezionata ────────────────
  const filteredData = useMemo(() => {
    if (!importId) return allData;
    return allData.filter(imp => imp.id === parseInt(importId, 10));
  }, [allData, importId]);

  // ── Paginazione ─────────────────────────────────────────────────────────
  const totalFiltered = filteredData.length;
  const pagedData = filteredData.slice((page - 1) * LIMIT, page * LIMIT);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="listiniforni-page">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Listini Fornitori</h1>
        </div>

        {/* Filtri */}
        <div className="bop-card">
          <div className="bop-filter-bar">
            <div className="bop-form-group">
              <label className="bop-label">Fornitore</label>
              <select
                className="bop-select"
                value={fornitoreId}
                onChange={(e) => setFornitoreId(e.target.value)}
              >
                <option value="">Tutti</option>
                {fornitori.map(f => (
                  <option key={f.id} value={f.id}>{f.ragioneSociale || f.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Importazione</label>
              <select
                className="bop-select"
                value={importId}
                onChange={(e) => { setImportId(e.target.value); setPage(1); }}
              >
                <option value="">Tutte</option>
                {allData.map(imp => (
                  <option key={imp.id} value={imp.id}>
                    {imp.etichetta} — {new Date(imp.importatoAt).toLocaleString('it-IT')}
                  </option>
                ))}
              </select>
            </div>
            <div className="bop-form-group listiniforni-filtri-azioni">
              <button
                className="bop-btn-secondary"
                onClick={() => {
                  setFornitoreId('');
                  setImportId('');
                  setPage(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Tabella */}
        <div className="bop-card">
          {loading ? (
            <div className="bop-empty-state">Caricamento...</div>
          ) : pagedData.length === 0 ? (
            <div className="bop-empty-state">Nessuna importazione trovata</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Importazione</th>
                  <th>Fornitore</th>
                  <th>Note</th>
                  <th>Utente</th>
                  <th>N° Righe</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map(imp => (
                  <tr key={imp.id}>
                    <td>{imp.etichetta}</td>
                    <td>{imp.fornitore?.ragioneSociale || imp.fornitore?.nome || '-'}</td>
                    <td className="bop-text-muted">{imp.note || '-'}</td>
                    <td>{imp.utente?.nome || '-'}</td>
                    <td className="bop-text-right">{imp.totaleRighe}</td>
                    <td>{badgeStato(imp.stato)}</td>
                    <td>
                      <button
                        className="bop-btn-secondary amministrazione-btn-small"
                        onClick={() => window.open(`/anagrafiche/articoli/listini-fornitori/${imp.id}`, '_blank')}
                      >
                        Apri
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginazione */}
          {totalFiltered > LIMIT && (
            <div className="listiniforni-paginazione">
              <span className="bop-text-muted">
                {(page - 1) * LIMIT + 1}–
                {Math.min(page * LIMIT, totalFiltered)} di {totalFiltered}
              </span>
              <button
                className="bop-btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Prec
              </button>
              <button
                className="bop-btn-secondary"
                disabled={page * LIMIT >= totalFiltered}
                onClick={() => setPage(page + 1)}
              >
                Succ →
              </button>
            </div>
          )}
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
